/**
 * InstructionJobOrchestratorService
 *
 * Orchestrates long-running async jobs with visual status feedback.
 * Handles polling, status updates, and timer cleanup in a framework-agnostic way.
 *
 * This service is designed to support React migration - business logic is separated
 * from AngularJS dependencies.
 *
 * Usage:
 *   const orchestrator = new InstructionJobOrchestratorService($interval, $timeout, $translate);
 *   orchestrator.runJob({
 *       job: () => service.generateInstructions(context),
 *       onSuccess: (results) => { this.results = results; },
 *       onStatusUpdate: (status) => { this.statusText = status.statusText; },
 *       onError: (errorConfig) => { this.showError(errorConfig); },
 *       onCompletion: () => { this.viewState = 'optimized'; },
 *       pollingIntervalMs: 2000
 *   });
 */
export class InstructionJobOrchestratorService {
    static $inject = ['$interval', '$timeout', '$translate'];
    constructor($interval, $timeout, $translate) {
        this.$interval = $interval;
        this.$timeout = $timeout;
        this.$translate = $translate;
        this.statusIntervalPromise = null;
        this.statusTimeoutPromise = null;
        this.isDestroyed = false;
        this.isCancelled = false;
    }
    /**
     * Run an async job with orchestrated polling and status updates.
     *
     * @param job The async function to execute (returns JobResult)
     * @param onSuccess Callback when job completes successfully
     * @param onStatusUpdate Callback when status text should be updated
     * @param onError Callback when job fails
     * @param onCompletion Callback after success status animation completes
     * @param pollingIntervalMs How often to increment status (default 2000ms)
     */
    runJob(job, onSuccess, onStatusUpdate, onError, onCompletion, pollingIntervalMs = 2000) {
        this.clearTimers();
        if (this.isDestroyed) {
            throw new Error('InstructionJobOrchestratorService is destroyed and cannot run new jobs.');
        }
        this.isCancelled = false;
        let isCompleted = false;
        let hasError = false;
        let currentStatusIndex = 0;
        // Initial status
        this.updateStatus(currentStatusIndex, onStatusUpdate);
        // Start polling
        this.statusIntervalPromise = this.$interval(() => {
            if (this.isDestroyed || this.isCancelled) {
                this.clearInterval();
                return;
            }
            if (hasError) {
                this.clearInterval();
                return;
            }
            if (isCompleted) {
                // Job is done, show completed state
                this.updateStatus('completed', onStatusUpdate);
                this.clearInterval();
                // After a brief delay, signal completion
                this.statusTimeoutPromise = this.$timeout(() => {
                    if (!this.isDestroyed && !this.isCancelled) {
                        onCompletion();
                    }
                }, 500);
                return;
            }
            // Increment status indicator (cycle through 1→2→3→1...)
            currentStatusIndex = currentStatusIndex < 3 ? currentStatusIndex + 1 : 1;
            this.updateStatus(currentStatusIndex, onStatusUpdate);
        }, pollingIntervalMs);
        // Execute the job
        job()
            .then((response) => {
            isCompleted = true;
            if (!this.isDestroyed && !this.isCancelled) {
                onSuccess(response.prompt);
            }
        })
            .catch(() => {
            hasError = true;
            isCompleted = true;
            if (!this.isDestroyed && !this.isCancelled) {
                onError();
            }
        });
    }
    /**
     * Cancel the currently running job.
     * Cleans up all timers and prevents further status updates.
     */
    cancel() {
        this.isCancelled = true;
        this.clearTimers();
    }
    /**
     * Cleanup when component is destroyed.
     * Prevents race conditions where callbacks execute on destroyed scope.
     */
    destroy() {
        this.isDestroyed = true;
        this.clearTimers();
    }
    /**
     * Initialize or reset the orchestrator state.
     */
    init() {
        this.isDestroyed = false;
        this.isCancelled = false;
        this.clearTimers();
    }
    /**
     * Update the current status text based on the status index.
     * Translates the status message and invokes the callback.
     *
     * @internal
     */
    updateStatus(statusIndex, onStatusUpdate) {
        if (this.statusTimeoutPromise) {
            this.$timeout.cancel(this.statusTimeoutPromise);
            this.statusTimeoutPromise = null;
        }
        const translationKey = statusIndex === 'completed'
            ? 'instructions.generatingInstructions.status.completed'
            : `instructions.generatingInstructions.status.${statusIndex}`;
        const currentText = this.$translate.instant(translationKey);
        // CRITICAL: Guard against race condition where component is destroyed before timeout fires
        this.statusTimeoutPromise = this.$timeout(() => {
            if (!this.isDestroyed && !this.isCancelled) {
                onStatusUpdate({
                    statusIndex,
                    statusText: currentText,
                });
            }
        });
    }
    /**
     * Clear all active timers.
     * @internal
     */
    clearTimers() {
        this.clearInterval();
        if (this.statusTimeoutPromise) {
            this.$timeout.cancel(this.statusTimeoutPromise);
            this.statusTimeoutPromise = null;
        }
    }
    /**
     * Clear the polling interval.
     * @internal
     */
    clearInterval() {
        if (this.statusIntervalPromise) {
            this.$interval.cancel(this.statusIntervalPromise);
            this.statusIntervalPromise = null;
        }
    }
}
InstructionJobOrchestratorService.$inject = ['$interval', '$timeout', '$translate'];
